import { useRouter } from 'next/navigation';
import { useTrainingAtom, HairColor } from '@/atoms/useTrainingAtom';
import { api } from '@/trpc/react';
import { useTrackClientEvent } from '@analytics/trackClientEvent';
import { Model } from '@acme/db';
import { SkinTone, DO_OVER_PRODUCT_NAME, slugify, FACIAL_HAIR_STYLES, HAIR_COLORS } from '@acme/lib';
import { improveResultsDialogOpenAtom } from '@/atoms/improveResultsDialog';
import { useSetAtom } from 'jotai';
import { sendGTMEvent } from '@components/GoogleTagManager';

interface UseModelDoOverProps {
  teamId?: string;
  returnUrl?: string;
}

export function useModelDoOver({ teamId, returnUrl: customReturnUrl }: UseModelDoOverProps) {
  const router = useRouter();
  const setImproveResultsDialogOpen = useSetAtom(improveResultsDialogOpenAtom);
  const { setTraining } = useTrainingAtom();
  const { trackEvent } = useTrackClientEvent();
  const userCreditsQuery = api.userCredits.get.useQuery({}, { enabled: !teamId });
  const teamMemberCreditsQuery = api.teamMemberCredits.get.useQuery({ teamId: teamId! }, { enabled: !!teamId });

  const hasDoOverCredits = teamId
    ? teamMemberCreditsQuery.data?.redoCredits && teamMemberCreditsQuery.data.redoCredits > 0
    : userCreditsQuery.data?.redoCredits && userCreditsQuery.data.redoCredits > 0;

  const handleDoOver = async (
    model?: Pick<Model, 'id' | 'trainingMeta' | 'attributes' | 'selfieUrl'>,
    name?: string
  ) => {
    if (hasDoOverCredits && model) {
      const meta = model.trainingMeta;
      console.log('model.attributes', model.attributes);

      setTraining({
        name: name || '',
        attributes: {
          eyeColor: model.attributes?.eyeColor || '',
          gender: model.attributes?.gender || '',
          glasses: model.attributes?.glasses || false,
          age: Number(model.attributes?.age) || undefined,
          hairColor: model.attributes?.hairColor as HairColor,
          hairStyle: model.attributes?.hairStyle as string,
          skinTone: model.attributes?.skinTone as SkinTone,
          // hair: model.attributes?.hair || false, // Legacy - now derived from hairStyle
          // facialHair: model.attributes?.facialHair || false, // Legacy - now derived from facialHairStyle
          facialHairStyle: model.attributes?.facialHairStyle as (typeof FACIAL_HAIR_STYLES)[number],
          facialHairColor: model.attributes?.facialHairColor as (typeof HAIR_COLORS)[number]
        },

        selfieUrl: model.selfieUrl || '',
        photos:
          meta?.trainingImagesInfo
            ?.filter(info => info.source === 'user_upload')
            .map(info => ({
              id: info.imageId,
              fileName: info.fileName || '',
              uploadedUrl: info.croppedImageUrl,
              originalUrl: info.originalImageUrl,
              maskedImageUrl: info.originalWithHiddenFaces,
              status: 'success',
              resolution: info.resolution ? { w: info.resolution.width, h: info.resolution.height } : undefined,
              caption: info.caption,
              age: info.ageRange ? (info.ageRange.Low + info.ageRange.High) / 2 : undefined
            })) || [],

        themes: [],

        parentId: model.id
      });

      setImproveResultsDialogOpen({ open: false, modelId: model.id });
      if (teamId) {
        router?.push(`/t/${teamId}/do-over?modelId=${model.id}`);
      } else {
        router?.push(`/dashboard/${model.id}/do-over`);
      }
    } else {
      try {
        sendGTMEvent({
          event: 'initiateCheckout',
          value: '14.99',
          discount_applied: false
        });

        trackEvent('initiateCheckout', {
          value: '14.99',
          metadata: {
            productName: 'Do-Over',
            itemCount: 1
          }
        });

        const searchParams = new URLSearchParams({
          returnUrl:
            customReturnUrl ??
            (teamId
              ? model
                ? `/t/${teamId}/do-over/model/${model.id}`
                : `/t/${teamId}/do-over`
              : model
              ? `/do-over/model/${model.id}`
              : `/do-over`),
          quantity: '1',
          ...(model && { modelId: model.id }),
          ...(teamId && { teamId })
        });

        router?.push(`/payment/add-on/checkout/${slugify(DO_OVER_PRODUCT_NAME)}?${searchParams.toString()}`);
      } catch (error) {
        console.error('Error navigating to checkout:', error);
      }
    }
  };

  return {
    handleDoOver,
    hasDoOverCredits,
    userCreditsQuery,
    teamMemberCreditsQuery
  };
}
